/**
 * Bonus bundles: one free bundle per order, picked by what the cart is worth.
 *
 * Keeps exactly one bonus bundle in the cart: the one for the highest tier the cart has
 * reached (Theme settings > Bonus bundles). Going up a tier swaps the lower bundle out,
 * coming back down swaps it back or removes it, and a bundle is always one unit. The
 * bundles' own prices never count towards a tier, so a bundle can't lift the cart into
 * the next one.
 *
 * Making the bundle free is a Shopify discount's job, not this script's. A bundle that
 * comes back from the cart still charged is taken straight out again, and is not tried
 * again until the cart total changes. A missing, expired or ineligible discount then costs
 * the shopper nothing. The browser console says which bundle and why.
 *
 * Every cart change made on the page is watched, whether it goes through fetch or
 * XMLHttpRequest: the product form, the drawer and cart page, quick add, the quick order
 * list, Colour Quick Order, Globo and apps. Once the change has settled, the cart is
 * checked, and it is checked again on every page load. When the cart has to change, the
 * drawer, the cart page and the header count are redrawn in place, and the drawer is not
 * opened. A checkout button pressed mid-check waits for it to finish.
 *
 * Shopify gotchas this works around, both confirmed on amr.com.au:
 * - /cart?sections=... sent with Accept: application/json answers with the cart and
 *   ignores `sections`, so section requests go without that header.
 * - A line key goes stale whenever the cart changes (the bundle discounts even split and
 *   re-key lines), and /cart/update.js then rejects the whole update with a 422.
 *
 * The tiers are the JSON in #bonus-bundles-config, written by snippets/bonus-bundles.liquid.
 */
(function () {
  'use strict';

  var configElement = document.getElementById('bonus-bundles-config');
  if (!configElement || window.bonusBundles) return;

  var config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    console.warn('[Bonus bundles] The config could not be read, so no bundles will be added.', error);
    return;
  }

  /* Tiers are set in the shop's currency; the cart is in the shopper's. */
  var rate = parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
  var tiers = (config.tiers || [])
    .map(function (tier) {
      return {
        min: Math.round(tier.min * rate),
        productId: tier.productId,
        variantId: tier.variantId,
        title: tier.title,
      };
    })
    .sort(function (a, b) {
      return a.min - b.min;
    });
  if (!tiers.length) return;

  var bundleProducts = {};
  tiers.forEach(function (tier) {
    bundleProducts[tier.productId] = true;
  });

  var cartUrl = config.routes.cart;
  var cartUpdateUrl = config.routes.cartUpdate + '.js';

  /* Captured before it is wrapped below, so this script's own requests never come back
     round as cart changes to react to. Globo's /cart.js listener in theme.liquid is
     already installed and still sees them. */
  var pageFetch = window.fetch;

  /* -------------------------------------------------------------- *
   * Bundles Shopify would not make free
   * -------------------------------------------------------------- */

  /* variant id -> the cart total it was refused at. Kept for the browser session so a
     page load does not retry it. */
  var STORE_KEY = 'bonus-bundles:refused';
  var refused = {};
  try {
    refused = JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
  } catch (error) {}

  function refuse(tier, subtotal, reason) {
    refused[tier.variantId] = subtotal;
    try {
      sessionStorage.setItem(STORE_KEY, JSON.stringify(refused));
    } catch (error) {}
    console.warn(
      '[Bonus bundles] "' +
        tier.title +
        '" ' +
        reason +
        ', so it has been left out of the cart. It will be tried again when the cart total changes. ' +
        'Check that a Shopify discount makes it free for this customer.'
    );
  }

  /* -------------------------------------------------------------- *
   * What the cart should hold
   * -------------------------------------------------------------- */

  function tierFor(subtotal) {
    /* A cart with nothing else in it never gets a bundle, whatever the lowest tier is set to. */
    if (subtotal <= 0) return null;
    var match = null;
    tiers.forEach(function (tier) {
      if (subtotal >= tier.min) match = tier;
    });
    if (match && refused[match.variantId] === subtotal) return null;
    return match;
  }

  /**
   * The changes that bring this cart to one unit of the right bundle and nothing else.
   * `updates` is keyed by line key for lines already in the cart, and by variant id for
   * the bundle to add. /cart/update.js takes both at once, and adds a variant id that is
   * not in the cart yet.
   */
  function planFor(cart) {
    var subtotal = 0;
    var bundleLines = [];

    /* Gift cards don't count towards a tier. */
    cart.items.forEach(function (item) {
      if (bundleProducts[item.product_id]) bundleLines.push(item);
      else if (!item.gift_card) subtotal += item.final_line_price;
    });

    var tier = tierFor(subtotal);
    var keep = null;
    var updates = {};

    bundleLines.forEach(function (item) {
      if (tier && !keep && item.variant_id === tier.variantId) {
        keep = item;
        if (item.quantity !== 1) updates[item.key] = 1;
      } else {
        updates[item.key] = 0;
      }
    });

    var add = tier && !keep ? tier : null;
    if (add) updates[add.variantId] = 1;

    return { subtotal: subtotal, tier: tier, keep: keep, add: add, updates: updates };
  }

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Bring the cart into line, starting from `cart` when the page's own last change has
   * already returned it. Resolves to the last cart a change returned, re-rendered
   * sections included, or null if nothing had to change.
   */
  async function reconcile(cart) {
    cart = cart || (await getCart());
    var sections = drawTargets().map(function (target) {
      return target.section;
    });
    var last = null;
    var retried = false;

    function changeBody(updates) {
      var body = { updates: updates };
      if (sections.length) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }
      return body;
    }

    /* One pass makes the changes and the next confirms the bundle came back free. The
       spares cover a retry and taking out a bundle that wasn't. */
    for (var pass = 0; pass < 5; pass++) {
      var plan = planFor(cart);

      if (!Object.keys(plan.updates).length) {
        if (!plan.keep || plan.keep.final_line_price === 0) break;
        refuse(plan.tier, plan.subtotal, 'is still charged ' + money(plan.keep.final_line_price));
        continue;
      }

      try {
        cart = await postJSON(cartUpdateUrl, changeBody(plan.updates));
      } catch (error) {
        /* Most likely a stale line key: look at the cart again before blaming the bundle. */
        if (!retried) {
          retried = true;
          cart = await getCart();
          continue;
        }
        if (!plan.add) throw error;
        refuse(plan.add, plan.subtotal, 'could not be added (' + error.message + ')');
        continue;
      }
      last = cart;

      if (
        plan.add &&
        !cart.items.some(function (item) {
          return item.variant_id === plan.add.variantId;
        })
      ) {
        refuse(plan.add, plan.subtotal, 'could not be added, and may be sold out');
      }
    }

    return last;
  }

  /* -------------------------------------------------------------- *
   * Redrawing the theme's cart
   * -------------------------------------------------------------- */

  /**
   * The cart sections on this page, and where each one goes. These are the swaps the
   * theme makes after a quantity change (cart.js), so the drawer is redrawn without
   * being opened.
   */
  function drawTargets() {
    var targets = [];
    var bubble = document.getElementById('cart-icon-bubble');
    var pageItems = document.getElementById('main-cart-items');
    var pageFooter = document.getElementById('main-cart-footer');

    if (document.querySelector('cart-drawer')) {
      targets.push({ section: 'cart-drawer', selector: '.drawer__inner', target: '#CartDrawer .drawer__inner' });
    }
    if (bubble) {
      targets.push({
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
        target: bubble.querySelector('.shopify-section') ? '#cart-icon-bubble .shopify-section' : '#cart-icon-bubble',
      });
    }
    if (pageItems && pageItems.dataset.id) {
      targets.push({ section: pageItems.dataset.id, selector: '.js-contents', target: '#main-cart-items .js-contents' });
    }
    if (pageFooter && pageFooter.dataset.id) {
      targets.push({ section: pageFooter.dataset.id, selector: '.js-contents', target: '#main-cart-footer .js-contents' });
    }
    return targets;
  }

  function paint(sections, cart) {
    /* Noted before the redraw, to find the same control in its new row afterwards. */
    var active = document.activeElement;
    var activeRow = active && active.closest ? active.closest('.cart-item[id]') : null;
    var activeName = active && active.getAttribute ? active.getAttribute('name') : null;
    var parser = new DOMParser();

    drawTargets().forEach(function (target) {
      var html = sections && sections[target.section];
      var element = document.querySelector(target.target);
      if (!html || !element) return;
      var source = parser.parseFromString(html, 'text/html').querySelector(target.selector);
      if (source) element.innerHTML = source.innerHTML;
    });

    var empty = cart.item_count === 0;
    var drawer = document.querySelector('cart-drawer');
    var pageCart = document.querySelector('cart-items');
    var pageFooter = document.getElementById('main-cart-footer');
    if (drawer) drawer.classList.toggle('is-empty', empty);
    if (pageCart) pageCart.classList.toggle('is-empty', empty);
    if (pageFooter) pageFooter.classList.toggle('is-empty', empty);

    /* Whatever had focus may just have been replaced. Put it back on its successor, and
       re-trap it if the drawer is open. */
    if (active && active !== document.body && !active.isConnected) {
      var successor =
        (active.id && document.getElementById(active.id)) ||
        (activeRow && activeName && document.querySelector('#' + activeRow.id + ' [name="' + activeName + '"]'));
      if (drawer && drawer.classList.contains('active') && typeof trapFocus === 'function') {
        trapFocus(document.getElementById('CartDrawer') || drawer, successor || drawer.querySelector('.drawer__inner'));
      } else if (successor) {
        successor.focus();
      }
    }
  }

  /** Redraw from a fresh render, for when the page drew the cart while a check was on. */
  async function draw() {
    var ids = drawTargets().map(function (target) {
      return target.section;
    });
    if (!ids.length) return;

    var results = await Promise.all([pageFetch(cartUrl + '?sections=' + ids.join(',')).then(readJSON), getCart()]);
    paint(results[0], results[1]);
  }

  /* -------------------------------------------------------------- *
   * Requests
   * -------------------------------------------------------------- */

  function readJSON(response) {
    return response.json().then(function (data) {
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'HTTP ' + response.status);
      return data;
    });
  }

  function getCart() {
    return pageFetch(cartUrl + '.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(readJSON);
  }

  /* keepalive, so a change already sent still lands if the shopper leaves the page. */
  function postJSON(url, body) {
    return pageFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      keepalive: true,
    }).then(readJSON);
  }

  /* -------------------------------------------------------------- *
   * Watching the page's own cart requests
   * -------------------------------------------------------------- */

  /**
   * 'change' for anything that edits the cart, 'draw' for a cart section being
   * re-rendered (the theme fetches /cart?section_id=cart-drawer after an add). A redraw
   * of the theme's that lands after one of ours would undo it, so both are waited for.
   * Other sections are left out: the cart page's "You may also like" re-renders itself
   * through /cart five times after every change, and waiting on that cost 4.5 seconds.
   */
  var CART_SECTION = /cart-(drawer|icon-bubble|items|footer|notification|live-region)/;

  function classify(url) {
    var parsed;
    try {
      parsed = new URL(url, window.location.href);
    } catch (error) {
      return null;
    }
    if (parsed.origin !== window.location.origin) return null;
    if (/\/cart\/(add|change|update|clear)(\.js|\.json)?\/?$/.test(parsed.pathname)) return 'change';
    if (CART_SECTION.test(parsed.searchParams.get('section_id') || parsed.searchParams.get('sections') || '')) {
      return 'draw';
    }
    return null;
  }

  /** The cart, if this response body is one: change.js and update.js answer with it, add.js doesn't. */
  function asCart(body) {
    try {
      var data = typeof body === 'string' ? JSON.parse(body) : body;
      return data && Array.isArray(data.items) && typeof data.item_count === 'number' ? data : null;
    } catch (error) {
      return null;
    }
  }

  var inFlight = 0; // the page's own cart requests still on the wire
  var running = false; // a check or redraw is under way
  var checking = false; // a check is under way
  var overlapped = false; // the page sent a cart request while this check was on
  var wantCheck = false; // the cart has changed since the last check began
  var wantDraw = false; // the page drew the cart while a check was on
  var timer = null;
  var idleWaiters = [];
  var changesSinceCheck = 0;
  var lastChangeCart = null;

  function watch(kind) {
    inFlight++;
    if (running) overlapped = true;
    var request = { kind: kind, overlap: running, done: false, cart: null };
    /* A request that never answers must not hold every later check up. */
    request.timer = setTimeout(function () {
      settle(request);
    }, 15000);
    return request;
  }

  /* Called once the page has its answer and has had a turn to act on it (say, by asking
     for a fresh drawer), so that follow-up is counted before the next check starts. */
  function settle(request) {
    if (request.done) return;
    request.done = true;
    clearTimeout(request.timer);
    inFlight--;
    if (request.kind === 'change') {
      wantCheck = true;
      changesSinceCheck++;
      lastChangeCart = request.overlap ? null : request.cart;
    }
    if (request.overlap) wantDraw = true;
    if (!inFlight) {
      idleWaiters.splice(0).forEach(function (resolve) {
        resolve();
      });
    }
    schedule();
  }

  function settleSoon(request, body) {
    if (request.kind === 'change') request.cart = asCart(body);
    setTimeout(function () {
      settle(request);
    }, 0);
  }

  function whenIdle() {
    if (!inFlight) return Promise.resolve();
    return new Promise(function (resolve) {
      idleWaiters.push(resolve);
    });
  }

  /** The cart the page's last change returned, when it was the only change since the last check. */
  function takeKnownCart() {
    var cart = changesSinceCheck === 1 ? lastChangeCart : null;
    changesSinceCheck = 0;
    lastChangeCart = null;
    return cart;
  }

  /* The short wait gathers a burst of changes into one check. */
  function schedule(delay) {
    if (running || timer) return;
    timer = setTimeout(function () {
      timer = null;
      if (running || inFlight || !(wantCheck || wantDraw)) return;
      run();
    }, delay === undefined ? 50 : delay);
  }

  async function run() {
    running = true;
    try {
      while (wantCheck || wantDraw) {
        await whenIdle();
        if (wantCheck) {
          wantCheck = false;
          overlapped = false;
          checking = true;
          var result = await reconcile(takeKnownCart());
          checking = false;
          /* The changes came back with the cart already drawn. Use that, unless the page
             drew it too in the meantime, in which case draw last from a fresh render. */
          if (result && (overlapped || inFlight)) wantDraw = true;
          else if (result) paint(result.sections, result);
        }
        if (wantDraw) {
          await whenIdle();
          wantDraw = false;
          await draw();
        }
      }
    } catch (error) {
      wantCheck = wantDraw = false;
      console.error('[Bonus bundles] The cart could not be checked.', error);
    } finally {
      running = checking = false;
    }
  }

  window.fetch = function (input, init) {
    var kind = classify(input instanceof Request ? input.url : String(input));
    if (!kind) return pageFetch.apply(window, arguments);

    var request = watch(kind);
    var result;
    try {
      result = pageFetch.apply(window, arguments);
    } catch (error) {
      settle(request);
      throw error;
    }
    result.then(
      function (response) {
        /* Read a copy, so this waits for the body the page itself is reading. */
        var copy;
        try {
          copy = response.clone();
        } catch (error) {
          return settleSoon(request, null);
        }
        copy.text().then(
          function (body) {
            settleSoon(request, body);
          },
          function () {
            settleSoon(request, null);
          }
        );
      },
      function () {
        settle(request);
      }
    );
    return result;
  };

  var xhrOpen = XMLHttpRequest.prototype.open;
  var xhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this._bonusBundlesKind = classify(String(url));
    return xhrOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    if (!this._bonusBundlesKind) return xhrSend.apply(this, arguments);
    var xhr = this;
    var request = watch(this._bonusBundlesKind);
    this.addEventListener('loadend', function () {
      var body = null;
      try {
        body = xhr.responseType === 'json' ? xhr.response : xhr.responseText;
      } catch (error) {}
      settleSoon(request, body);
    });
    try {
      return xhrSend.apply(this, arguments);
    } catch (error) {
      settle(request);
      throw error;
    }
  };

  /* -------------------------------------------------------------- *
   * Checkout
   * -------------------------------------------------------------- */

  function busy() {
    return checking || wantCheck || inFlight > 0 || timer !== null;
  }

  function whenSettled() {
    return new Promise(function (resolve) {
      var waited = 0;
      (function poll() {
        if (!busy() || waited >= 20000) return resolve();
        waited += 100;
        setTimeout(poll, 100);
      })();
    });
  }

  /* Checking out mid-check could take the wrong bundle, or one that is no longer free,
     to checkout. The drawer's and cart page's Check out buttons wait for it instead. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      var submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout' || !busy()) return;
      event.preventDefault();
      submitter.setAttribute('aria-disabled', 'true');
      whenSettled().then(function () {
        submitter.removeAttribute('aria-disabled');
        if (form.requestSubmit) form.requestSubmit(submitter);
        else submitter.click();
      });
    },
    true
  );

  function check() {
    wantCheck = true;
    schedule(0);
  }

  /* A page restored from the back/forward cache may be showing a cart that has changed since. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) check();
  });

  window.bonusBundles = { check: check, tiers: tiers };
  check();
})();
